import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const DEFAULT_FROM_TIME = '00:00:00';
const DEFAULT_TO_TIME = '23:59:59';
const VALID_STATUSES = new Set(['all', 'pending', 'completed', 'cancelled']);
const MULTI_VALUE_FILTERS = ['statuses', 'method', 'fiatCurrency', 'traderName', 'gateway'];
const SINGLE_VALUE_FILTERS = ['amountFiat', 'amountUsdt', 'price'];

const parsePositiveInteger = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

const getBrowserTimezone = () => String(-new Date().getTimezoneOffset());

const setOptionalParam = (params, key, value, defaultValue = '') => {
  const normalized = value === null || value === undefined ? '' : String(value);
  if (!normalized || normalized === defaultValue) params.delete(key);
  else params.set(key, normalized);
};

const setMultiValueParam = (params, key, values) => {
  params.delete(key);
  if (!Array.isArray(values)) return;

  values.forEach((value) => {
    const normalized = String(value ?? '').trim();
    if (normalized) params.append(key, normalized);
  });
};

const readFilters = (searchParams) => {
  const filters = {};

  MULTI_VALUE_FILTERS.forEach((key) => {
    const values = searchParams.getAll(key).filter(Boolean);
    if (values.length) filters[key] = values;
  });
  SINGLE_VALUE_FILTERS.forEach((key) => {
    const value = searchParams.get(key);
    if (value !== null && value !== '') filters[key] = value;
  });

  return filters;
};

const writeFilters = (params, filters = {}) => {
  MULTI_VALUE_FILTERS.forEach((key) => setMultiValueParam(params, key, filters[key]));
  SINGLE_VALUE_FILTERS.forEach((key) => setOptionalParam(params, key, filters[key]));
};

const getFilterQuery = (searchParams) => {
  const filterParams = new URLSearchParams();
  MULTI_VALUE_FILTERS.forEach((key) => {
    searchParams.getAll(key).forEach((value) => filterParams.append(key, value));
  });
  SINGLE_VALUE_FILTERS.forEach((key) => {
    const value = searchParams.get(key);
    if (value !== null) filterParams.set(key, value);
  });
  return filterParams.toString();
};

const useUrlOrderFilters = (defaultLimit = 50) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const browserTimezone = getBrowserTimezone();
  const rawStatus = searchParams.get('status');
  const activeTab = VALID_STATUSES.has(rawStatus) ? rawStatus : 'all';
  const currentPage = parsePositiveInteger(searchParams.get('page'), 1);
  const itemsPerPage = parsePositiveInteger(searchParams.get('limit'), defaultLimit);
  const searchTerm = searchParams.get('search') || '';
  const dateFrom = searchParams.get('dateFrom') || '';
  const dateTo = searchParams.get('dateTo') || '';
  const dateFromTime = searchParams.get('dateFromTime') || DEFAULT_FROM_TIME;
  const dateToTime = searchParams.get('dateToTime') || DEFAULT_TO_TIME;
  const dateTimezone = searchParams.get('timezone') || browserTimezone;
  const filterQuery = getFilterQuery(searchParams);
  const orderFilters = useMemo(
    () => readFilters(new URLSearchParams(filterQuery)),
    [filterQuery]
  );

  const updateParams = useCallback((update, { replace = false } = {}) => {
    setSearchParams((previousParams) => {
      const nextParams = new URLSearchParams(previousParams);
      update(nextParams);
      nextParams.set('limit', String(itemsPerPage));
      return nextParams;
    }, { replace });
  }, [itemsPerPage, setSearchParams]);

  const setCurrentPage = useCallback((page) => {
    const nextPage = parsePositiveInteger(page, 1);
    updateParams((params) => {
      if (nextPage === 1) params.delete('page');
      else params.set('page', String(nextPage));
    });
  }, [updateParams]);

  const setActiveTab = useCallback((status) => {
    const nextStatus = VALID_STATUSES.has(status) ? status : 'all';
    updateParams((params) => {
      if (nextStatus === 'all') params.delete('status');
      else params.set('status', nextStatus);
      if (nextStatus !== 'all') params.delete('statuses');
      params.delete('page');
    });
  }, [updateParams]);

  const setSearchTerm = useCallback((value) => {
    updateParams((params) => {
      setOptionalParam(params, 'search', value);
      params.delete('page');
    }, { replace: true });
  }, [updateParams]);

  const applyFilters = useCallback(({
    dateFrom: nextDateFrom,
    dateTo: nextDateTo,
    dateFromTime: nextDateFromTime,
    dateToTime: nextDateToTime,
    dateTimezone: nextDateTimezone,
    filters: nextFilters
  }) => {
    updateParams((params) => {
      setOptionalParam(params, 'dateFrom', nextDateFrom);
      setOptionalParam(params, 'dateTo', nextDateTo);
      setOptionalParam(
        params, 'dateFromTime', nextDateFrom ? nextDateFromTime : '', DEFAULT_FROM_TIME
      );
      setOptionalParam(
        params, 'dateToTime', nextDateTo ? nextDateToTime : '', DEFAULT_TO_TIME
      );
      setOptionalParam(
        params, 'timezone', nextDateFrom || nextDateTo ? nextDateTimezone : '', browserTimezone
      );
      writeFilters(params, nextFilters);
      if (nextFilters?.statuses?.length) params.delete('status');
      params.delete('page');
    });
  }, [browserTimezone, updateParams]);

  return {
    activeTab,
    currentPage,
    itemsPerPage,
    searchTerm,
    orderFilters,
    dateFrom,
    dateTo,
    dateFromTime,
    dateToTime,
    dateTimezone,
    setCurrentPage,
    setActiveTab,
    setSearchTerm,
    applyFilters
  };
};

export default useUrlOrderFilters;
